import Link from 'next/link';
import { LearningPathCard, type LearningPathCardData } from '../common/learning-path-card-new';
import { getCoursesWithQuizCount, getVideosCountFromCourses } from '@/lib/utils/learning-path-counts';
import { getLearningPathCertificateLabel } from '@/lib/utils/learning-path-utils';

interface LearningPathStats {
  courses: number;
  videos: number;
  quizzes: number;
  assessments: number;
}

export interface LearningPathData {
  image: {
    url: string;
    alt?: string;
  };
  smallLabel: string;
  title: string;
  description: string;
  certificateLabel?: string;
  stats: LearningPathStats;
  isEnrolled: boolean;
  progressPercentage: number;
  isCompleted?: boolean;
  isLearningMaterialCompleted?: boolean;
  hasAssessment?: boolean;
  enrollLink?: string;
  resumeLink?: string;
}

interface SliderData {
  title?: string;
  link?: {
    title?: string;
    href?: string;
  };
  cards?: any[];
}

interface LearningPathSectionProps {
  slider?: SliderData;
  heading?: string;
  viewAllText?: string;
  viewAllLink?: string;
  learningPaths?: LearningPathData[];
}

// Helper function to transform CMS data to component format
function transformLearningPathData(slider?: SliderData): LearningPathData[] {
  if (!slider?.cards) return [];

  return slider.cards
    .map((cardItem: any): LearningPathData | null => {
      const learningPath = cardItem?.card?.[0];
      if (!learningPath) return null;

      const details = learningPath.learning_pathway_details || {};
      const courses = learningPath.courses ?? {};
      const courseList = learningPath.courses?.course || [];
      const courseCount = courseList.length;
      const videosCount =
        typeof details.videos_count === 'number' && details.videos_count > 0
          ? details.videos_count
          : getVideosCountFromCourses(courses);
      const quizzesCount = getCoursesWithQuizCount(courses);

      const stats: LearningPathStats = {
        courses: courseCount || 0,
        videos: videosCount || 0,
        quizzes: quizzesCount || 0,
        assessments: learningPath.is_assessment ? 1 : 0,
      };

      const certificateLabel = getLearningPathCertificateLabel(details.awards_certifications);

      const imageUrl = details.thumbnail?.url || learningPath.open_graph?.image?.url || '';
      const imageAlt = details.thumbnail?.title || learningPath.title || '';

      if (!imageUrl) return null;

      return {
        image: {
          url: imageUrl,
          alt: imageAlt,
        },
        smallLabel: 'Learning Path',
        title: learningPath.title || '',
        description: details.description || learningPath.open_graph?.description || '',
        certificateLabel,
        stats,
        isEnrolled: false, // Static for now
        progressPercentage: 0, // Static for now
        enrollLink: learningPath.url || '#',
      };
    })
    .filter((item): item is LearningPathData => item !== null);
}

export function LearningPathSection({
  slider,
  learningPaths: propLearningPaths = [],
}: LearningPathSectionProps) {
  // Extract data from slider if provided, otherwise use prop
  // Prioritize propLearningPaths if provided (from client component with progress)
  const headingValue = slider?.title || 'Featured Learning Path';
  const viewAllTextValue = slider?.link?.title || 'View all';
  const viewAllLinkValue = slider?.link?.href || '#';
  const learningPaths = propLearningPaths.length > 0 ? propLearningPaths : (slider ? transformLearningPathData(slider) : []);
  return (
    <section className="section w-full py-8 desktop:py-12">
      <div className="mx-auto w-full">
        {/* Header with heading and View all */}
        <div className="flex items-center justify-between mb-6 desktop:mb-8">
          <h2 className="cs-h6 tab:cs-h4 desktop:cs-h3 !text-text-default-medium" id="learning-paths-heading">{headingValue}</h2>
          <Link
            href={viewAllLinkValue}
            className="view-all-link cs-caption tab:cs-s-body desktop:cs-m-body !text-amethyst-accent"
            aria-label={`${viewAllTextValue} for ${headingValue}`}
          >
            {viewAllTextValue}
          </Link>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 tab:grid-cols-1 lg:grid-cols-2 gap-6 tab:gap-8 lg:gap-8 items-stretch">
          {learningPaths.map((path, index) => {
            const cardData: LearningPathCardData = {
              image: path.image,
              smallLabel: path.smallLabel,
              title: path.title,
              description: path.description,
              certificateLabel: path.certificateLabel,
              stats: path.stats,
              isEnrolled: path.isEnrolled,
              progressPercentage: path.progressPercentage,
              isCompleted: path.isCompleted,
              isLearningMaterialCompleted: path.isLearningMaterialCompleted,
              hasAssessment: path.hasAssessment,
              enrollLink: path.enrollLink ? `/learning-paths${path.enrollLink}` : undefined,
              resumeLink: path.resumeLink ? `/learning-paths${path.resumeLink}` : undefined,
            };

            return (
              <LearningPathCard
                key={index}
                path={cardData}
                variant="home"
                priority={index === 0}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
